'use client';

import { Badge, BodyDefault, MetaDefault } from '@terra/ui-design-system';
import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import { Key01Icon, UserIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { AiDiscoveryIdentityRowModel } from './ai-discovery-identity.util';

type AiDiscoveryIdentityRowProps = {
  row: AiDiscoveryIdentityRowModel;
  hideDivider?: boolean;
};

const BADGE_STYLE: Record<
  AiDiscoveryIdentityRowModel['badge'],
  { bg: string; color: string }
> = {
  ready: {
    bg: token('colors.bg.neutral.subtle'),
    color: 'fg.default',
  },
  authRequired: {
    bg: token('colors.bg.neutral.warning'),
    color: 'fg.default',
  },
  failed: {
    bg: token('colors.bg.neutral.error'),
    color: 'fg.default',
  },
  disabled: {
    bg: token('colors.bg.neutral.muted'),
    color: 'fg.muted',
  },
};

export function AiDiscoveryIdentityRow({
  row,
  hideDivider,
}: AiDiscoveryIdentityRowProps) {
  const t = useTranslations('discovery.aiApplication.identityBadge');
  const Icon = row.kind === 'user' ? UserIcon : Key01Icon;
  const badgeStyle = BADGE_STYLE[row.badge];

  return (
    <HStack
      w="full"
      px="3"
      py="2.5"
      gap="2"
      alignItems="center"
      justifyContent="space-between"
      borderBottomWidth={hideDivider ? undefined : '1px'}
      borderColor={hideDivider ? undefined : 'border.muted'}
    >
      <HStack gap="2" alignItems="center" minW="0" flex="1">
        <Box color="fg.muted" flexShrink={0} display="flex">
          <Icon size={16} />
        </Box>
        <VStack gap="0" alignItems="flex-start" minW="0">
          <BodyDefault truncate>{row.primaryLabel}</BodyDefault>
          {row.secondaryLabel ? (
            <MetaDefault color="fg.muted" truncate>
              {row.secondaryLabel}
            </MetaDefault>
          ) : null}
        </VStack>
      </HStack>
      <Badge
        size="sm"
        variant="fill"
        bg={badgeStyle.bg}
        color={badgeStyle.color}
        flexShrink={0}
      >
        {t(row.badge)}
      </Badge>
    </HStack>
  );
}
